import { EnumCacheEntityType } from "@/app/services/generated/enums/enumCacheEntityType.generated";
import type { Vessel } from "@/app/services/generated/vesselv2Service";
import { vesselService } from "@/app/services/generated/vesselv2Service";
import {
  EntityWithGuidKeyCache,
  EntityCacheStore,
} from "@/app/caching/apiCache";
import type {
  VesselWithCaskVessel,
  VesselWithProductionVessel,
} from "@/app/services/extensions/vesselv2ServiceExtended";
import type Guid from "@/app/types/common/guid";
import type { ComputedRef } from "vue";
import { computed } from "vue";

export interface IHasVesselsCache {
  vessels: VesselsCache;
}

// Singleton cache store
const vesselsCacheStore = new EntityCacheStore<Vessel>(
  EnumCacheEntityType.vessel,
  async () => vesselService.getAllVessels()
);

// Ephemeral reader
export default class VesselsCache extends EntityWithGuidKeyCache<Vessel> {
  private _allOrdered: ComputedRef<readonly Vessel[]>;

  constructor() {
    super(vesselsCacheStore, (x) => x.id);
    this._allOrdered = computed(() =>
      this.all
        .slice()
        .sort(
          (a, b) =>
            (a.productionVessel?.ordering ?? 0) -
            (b.productionVessel?.ordering ?? 0)
        )
        .sort(
          (a, b) => (a.productionVessel ? 0 : 1) - (b.productionVessel ? 0 : 1)
        )
    );
  }

  public get allOrdered() {
    return this._allOrdered.value;
  }

  public get allProductionVesselsOrderedReactive() {
    return Object.freeze(
      this.allOrdered.filter((x) => x.productionVessel)
    ) as readonly VesselWithProductionVessel[];
  }

  public get allCaskVesselsReactive() {
    return Object.freeze(
      this.allOrdered.filter(
        (x) => x.caskVessel
      ) as readonly Readonly<VesselWithCaskVessel>[]
    );
  }

  public allAssignedToProcess(processId: Guid) {
    return this.allOrdered.filter((x) =>
      x.readOnly.currentProcessId?.equals(processId)
    );
  }

  public allUnassigned() {
    return this.allOrdered.filter(
      (x) => !x.readOnly.currentAssemblyId && !x.readOnly.currentProcessId
    );
  }
}
